import dayjs from "dayjs";
import { Form, Formik, FormikHelpers } from "formik";
import { useCallback } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { FeedbackBanner } from "@/core/components/feedback-banner";
import { FormInput } from "@/core/components/form-input";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { Textarea } from "@/core/components/ui/textarea";
import { Typography } from "@/core/components/ui/typography";
import { useCreateIcmCellOverride, useClearIcmCellOverride } from "@/icm/query";
import type { IcmCellOverride, IcmLineItem } from "@/icm/types";
import { formatTraceValue, humanizeKey } from "@/icm/utils";

interface EditCellModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessId?: string | null;
  statementId?: string;
  lineItem: IcmLineItem;
  columnName: string;
  currentValue: unknown;
  existingOverride: IcmCellOverride | null;
}

interface EditCellFormValues {
  value: string;
  reason: string;
}

const validationSchema = Yup.object({
  value: Yup.string().required("Value is required"),
  reason: Yup.string().required("Reason is required"),
});

export const EditCellModal = ({
  open,
  onOpenChange,
  businessId,
  statementId,
  lineItem,
  columnName,
  currentValue,
  existingOverride,
}: EditCellModalProps) => {
  const { mutate: createOverride } = useCreateIcmCellOverride(
    businessId,
    statementId,
  );
  const { mutate: clearOverride, isPending: isClearPending } =
    useClearIcmCellOverride(businessId, statementId);

  const initialValues: EditCellFormValues = {
    value: existingOverride ? String(existingOverride.value) : "",
    reason: "",
  };
  const columnLabel = humanizeKey(columnName);

  const handleSubmit = useCallback(
    (
      values: EditCellFormValues,
      { setSubmitting }: FormikHelpers<EditCellFormValues>,
    ) => {
      createOverride(
        {
          payout_rule_id: lineItem.payout_rule_id,
          source_record_id: lineItem.source_record_id,
          column_name: columnName,
          value: values.value,
          reason: values.reason,
        },
        {
          onSuccess: () => {
            toast.success("Override saved");
            onOpenChange(false);
          },
          onError: (error) => {
            toast.error(error.message);
            setSubmitting(false);
          },
        },
      );
    },
    [createOverride, lineItem, columnName, onOpenChange],
  );

  const handleClearOverride = useCallback(() => {
    if (!existingOverride) return;
    clearOverride(
      { overrideId: existingOverride.id },
      {
        onSuccess: () => {
          toast.success("Override cleared");
          onOpenChange(false);
        },
        onError: (error) => {
          toast.error(error.message);
        },
      },
    );
  }, [existingOverride, clearOverride, onOpenChange]);

  const displayOriginalValue = formatTraceValue(
    columnName,
    existingOverride?.previous_value ?? currentValue,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        description={`Edit the ${columnLabel} value for this line item.`}
      >
        <DialogHeader>
          <DialogTitle>Edit {columnLabel}</DialogTitle>
        </DialogHeader>

        <Stack gap="md" className="px-6">
          <Stack gap="xs">
            <Typography variant="subtitleW3" textColor="tertiary">
              {existingOverride ? "Original Value" : "Current Value"}
            </Typography>
            <Typography variant="bodyW2">{displayOriginalValue}</Typography>
          </Stack>

          {existingOverride && (
            <Stack gap="xs">
              <Typography variant="subtitleW3" textColor="tertiary">
                Current Override
              </Typography>
              <Typography variant="bodyW2">
                {formatTraceValue(columnName, existingOverride.value)}
              </Typography>
              <Typography variant="bodyW3" textColor="secondary">
                Edited by {existingOverride.edited_by.full_name} on{" "}
                {dayjs(existingOverride.edited_at).format("MMM DD, YYYY")}
              </Typography>
              {existingOverride.stale && (
                <FeedbackBanner variant="warning" iconSize="sm">
                  <Typography variant="bodyW2">
                    This override may be stale — the underlying data has
                    changed.
                  </Typography>
                </FeedbackBanner>
              )}
            </Stack>
          )}
        </Stack>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnMount
        >
          {({ isValid, isSubmitting, getFieldProps }) => (
            <Form>
              <Stack gap="md" className="px-6 pb-2">
                <FormInput
                  label={`New ${columnLabel}`}
                  fullWidth
                  required
                  {...getFieldProps("value")}
                />
                <Textarea
                  label="Reason"
                  required
                  {...getFieldProps("reason")}
                />
              </Stack>

              <DialogFooter>
                {existingOverride && (
                  <Button
                    type="button"
                    variant="destructiveSecondary"
                    size="sm"
                    onClick={handleClearOverride}
                    loading={isClearPending}
                    disabled={isSubmitting || isClearPending}
                    className="mr-auto"
                  >
                    Clear override
                  </Button>
                )}
                <Button
                  type="button"
                  variant="tertiary"
                  size="sm"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={!isValid || isSubmitting || isClearPending}
                  loading={isSubmitting}
                >
                  Save override
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
